/*
 * /hand-arm-vibration-calculator — страница. Расчёт — core.js; нормы и
 * справочник HSE приходят со страницы (window.__HAV), которая печатает те же
 * таблицы сервером (resources/data/hav/*.json).
 *
 * Первый экран рисует блейд: строки по умолчанию, итог и вердикт. Скрипт,
 * проснувшись, берёт строки из ссылки (#s=…), из сохранённого или из
 * разметки (то, что человек успел набрать до скрипта), и дальше владеет ими.
 */
import * as H from './core.js';

const SLUG = 'hand-arm-vibration-calculator';
const W = window.__HAV || {};
H.setNorms(W.norms);
const I = W.i18n || {};
const TOOLS = {};
for (const g of (W.tools && W.tools.groups) || []) for (const t of g.tools) TOOLS[t.id] = t;
const $ = (s, r = document) => r.querySelector(s);
const tr = (k, rep = {}) => String(I[k] != null ? I[k] : k).replace(/:([a-z_]+)/g, (m, n) => (rep[n] != null ? rep[n] : m));
const esc = s => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const LOC = (window.__intlLocale && window.__intlLocale()) || 'en';
const RTL = document.documentElement.getAttribute('dir') === 'rtl';
const ltr = s => (RTL ? '\u2066' + s + '\u2069' : s);
// Число — изолированно слева направо, единица — в направлении языка (в ar единица своя: «م/ث²»).
const withU = (n, u) => ltr(n) + ' ' + tr(u);
const nf = {};
function num(x, d = 2) {
    if (!(x === x) || x == null || !isFinite(x)) return '—';
    if (!nf[d]) { try { nf[d] = new Intl.NumberFormat(LOC, { minimumFractionDigits: d, maximumFractionDigits: d }); } catch (e) { nf[d] = { format: v => v.toFixed(d) }; } }
    return nf[d].format(x);
}
// Величина без лишних нулей: «7», «3,5», «2,46».
function numA(x) { return num(x, Math.abs(x - Math.round(x)) < 1e-9 ? 0 : (Math.abs(x * 10 - Math.round(x * 10)) < 1e-9 ? 1 : 2)); }
// Норма — как её пишет документ: «2,0», «2,5», «5».
const numLim = x => num(x, Number.isInteger(x) && x >= 5 ? 0 : 1);
const dbStr = a => numA(Math.round(H.levelDb(a) * 10) / 10);
// Десятичная запятая или точка, пробелы — люди набирают «2,5».
function parse(v) {
    const s = String(v == null ? '' : v).trim().replace(/[\s  ]/g, '').replace(',', '.');
    if (!s) return NaN;
    const x = Number(s);
    return x === x ? x : NaN;
}
function dur(h) {
    if (h == null) return '—';
    if (!isFinite(h) || h > 24) return tr('dur_more');
    const mins = Math.floor(h * 60 + 1e-6);
    if (mins <= 0) return tr('dur_none');
    const hh = Math.floor(mins / 60), mm = mins % 60;
    if (!hh) return tr('dur_m', { m: mins });
    return mm ? tr('dur_hm', { h: hh, m: mm }) : tr('dur_h', { h: hh });
}

const store = {
    load: (k, d) => { try { return window.__toolSettings ? window.__toolSettings.load(SLUG, k, d) : d; } catch (e) { return d; } },
    save: (k, v) => { try { if (window.__toolSettings) window.__toolSettings.save(SLUG, k, v); } catch (e) { /* приватный режим */ } },
};
const T = (() => {
    let timer = null;
    const sent = new Set();
    return {
        touch(action) {
            if (sent.has(action)) return;
            sent.add(action);
            try { if (window.__ae) window.__ae('feature_use', { slug: SLUG, action }); } catch (e) { /* необязательно */ }
        },
        done(r) {
            clearTimeout(timer);
            timer = setTimeout(() => {
                try { if (window.__aeToolComplete) window.__aeToolComplete({ norm: S.norm, rows: r.rows.filter(x => x.valid).length, level: r.level }); } catch (e) { /* необязательно */ }
            }, 2000);
        },
    };
})();

const S = {
    norm: (W.cfg && W.cfg.norm) || 'eu',
    unit: 'ms2',
    factor: 1,
    floor: true,
    rows: [],
    rec: { name: '', job: '', site: '', date: '', by: '' },
};
let seq = 0;
// alt — то же значение в другой единице, пока человек его не правил: м/с² → дБ → м/с² возвращает набранное, а не округлённое.
const newRow = (o = {}) => Object.assign({ k: ++seq, lib: '', name: '', a: '', alt: null, declared: false, mode: 'hm', h: '', m: '', ops: '', sec: '' }, o);

/* ── состояние в ссылке ──────────────────────────────────────────────────── */
const ROW_KEYS = ['lib', 'name', 'a', 'declared', 'mode', 'h', 'm', 'ops', 'sec'];
function pack() {
    return {
        n: S.norm, u: S.unit, f: S.factor, fl: S.floor ? 1 : 0,
        r: S.rows.map(r => ROW_KEYS.map(k => (k === 'declared' ? (r[k] ? 1 : 0) : r[k]))),
    };
}
function unpack(o) {
    if (!o || typeof o !== 'object' || !Array.isArray(o.r)) return false;
    if (o.n && H.norm(o.n)) S.norm = o.n;
    S.unit = o.u === 'db' ? 'db' : 'ms2';
    S.factor = [1, 1.5, 2].includes(Number(o.f)) ? Number(o.f) : 1;
    S.floor = o.fl !== 0;
    S.rows = o.r.slice(0, 20).map(a => {
        const r = newRow();
        ROW_KEYS.forEach((k, i) => { if (a[i] != null) r[k] = k === 'declared' ? !!a[i] : String(a[i]).slice(0, 80); });
        if (r.mode !== 'ops') r.mode = 'hm';
        return r;
    });
    return true;
}
function b64urlEncode(str) {
    const bytes = new TextEncoder().encode(str);
    let bin = '';
    bytes.forEach(b => { bin += String.fromCharCode(b); });
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function b64urlDecode(s) {
    const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/'));
    const bytes = Uint8Array.from(bin, c => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
}
function fromHash() {
    const m = /[#&]s=([A-Za-z0-9_-]+)/.exec(location.hash || '');
    if (!m) return false;
    try { return unpack(JSON.parse(b64urlDecode(m[1]))); } catch (e) { return false; }
}
function shareUrl() {
    const u = new URL(location.href);
    u.hash = 's=' + b64urlEncode(JSON.stringify(pack()));
    return u.toString();
}

/* ── величина строки в м/с² (поле может быть в дБ) ─────────────────────── */
function rowA(r) {
    const v = parse(r.a);
    if (!(v > 0)) return 0;
    return S.unit === 'db' ? H.fromDb(v) : v;
}
function rowsForCore() {
    return S.rows.map(r => ({
        a: rowA(r), declared: r.declared, mode: r.mode,
        h: parse(r.h) || 0, m: parse(r.m) || 0, ops: parse(r.ops) || 0, sec: parse(r.sec) || 0,
    }));
}
const opts = () => ({ factor: S.factor, floor: S.floor });
const PAGE_LANG = window.__LOCALE || 'en';
const srcOf = n => (n.src_local && n.src_local[PAGE_LANG]) || n.src;
// Дата по часам человека, а не по Гринвичу (toISOString после полуночи даёт вчера).
function today() {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

/* ── строки ──────────────────────────────────────────────────────────────── */
function libHint(lib) {
    const t = TOOLS[lib];
    if (!t) return '';
    return tr('lib_hint', { lo: numA(t.lo), hi: numA(t.hi), a: numA(t.a) });
}
function rowHtml(r, i) {
    const u = S.unit === 'db' ? tr('u_db') : tr('u_ms2');
    const hm = r.mode !== 'ops';
    const hint = libHint(r.lib);
    return `<div class="hav-row" data-k="${r.k}">
  <div class="hav-row-top">
    <span class="hav-row-n" aria-hidden="true">${i + 1}</span>
    <label class="tc-f hav-name-f"><span class="tc-sr">${esc(tr('lbl_name'))}</span>
      <input class="tc-in hav-name" data-f="name" maxlength="80" autocomplete="off" value="${esc(r.name)}" placeholder="${esc(tr('ph_name'))}"></label>
    <button type="button" class="hav-del" data-act="del" aria-label="${esc(tr('row_remove'))}" title="${esc(tr('row_remove'))}">×</button>
  </div>
  <div class="hav-row-fields">
    <label class="tc-f hav-a-f"><span class="tc-f-l">${esc(tr('lbl_a', { u }))}</span>
      <input class="tc-in" data-f="a" inputmode="decimal" autocomplete="off" value="${esc(r.a)}" placeholder="${S.unit === 'db' ? '126' : '7'}"></label>
    <div class="tc-f hav-t-f">
      <div class="tc-seg hav-tmode" role="group" aria-label="${esc(tr('lbl_time'))}">
        <button type="button" class="tc-seg-b${hm ? ' on' : ''}" data-mode="hm" aria-pressed="${hm}">${esc(tr('mode_hm'))}</button>
        <button type="button" class="tc-seg-b${hm ? '' : ' on'}" data-mode="ops" aria-pressed="${!hm}">${esc(tr('mode_ops'))}</button>
      </div>
      <div class="hav-tf"${hm ? '' : ' hidden'}>
        <label class="hav-unit-in"><input class="tc-in" data-f="h" inputmode="numeric" autocomplete="off" value="${esc(r.h)}" placeholder="0" aria-label="${esc(tr('lbl_hours'))}"><span>${esc(tr('u_h'))}</span></label>
        <label class="hav-unit-in"><input class="tc-in" data-f="m" inputmode="numeric" autocomplete="off" value="${esc(r.m)}" placeholder="0" aria-label="${esc(tr('lbl_minutes'))}"><span>${esc(tr('u_min'))}</span></label>
      </div>
      <div class="hav-tf"${hm ? ' hidden' : ''}>
        <label class="hav-unit-in"><input class="tc-in" data-f="ops" inputmode="numeric" autocomplete="off" value="${esc(r.ops)}" placeholder="120" aria-label="${esc(tr('lbl_ops'))}"><span>×</span></label>
        <label class="hav-unit-in"><input class="tc-in" data-f="sec" inputmode="decimal" autocomplete="off" value="${esc(r.sec)}" placeholder="30" aria-label="${esc(tr('lbl_sec'))}"><span>${esc(tr('u_sec'))}</span></label>
      </div>
    </div>
  </div>
  <label class="hav-decl"><input type="checkbox" data-f="declared"${r.declared ? ' checked' : ''}> <span>${esc(tr('lbl_declared'))}</span></label>
  <p class="tc-hint hav-lib"${hint ? '' : ' hidden'}>${esc(hint)}</p>
  <p class="hav-out" aria-live="polite"></p>
</div>`;
}
function renderRows() {
    const host = $('#havRows');
    host.innerHTML = S.rows.map(rowHtml).join('');
    $('#havEmpty').hidden = S.rows.length > 0;
}

/* ── итог ────────────────────────────────────────────────────────────────── */
function rowOut(r, x, n) {
    if (!x.valid) return x.a > 0 ? tr('out_need_time') : tr('out_need_a');
    const parts = [];
    if (S.rows[r] && S.rows[r].declared && Math.abs(x.a - rowA(S.rows[r])) > 1e-9) parts.push(tr('out_used', { a: withU(numA(x.a), 'u_ms2') }));
    if (n && n.points) parts.push(tr('out_points', { p: num(x.points, 0), pph: num(x.pph, 0) }));
    parts.push(tr('out_a8', { v: withU(num(x.a8), 'u_ms2') }));
    // «Сколько ещё можно сегодня» — с учётом всего, что уже набрано в смене.
    const left = [];
    const item = (k, h) => left.push(h > 0 ? tr('left_' + k, { t: dur(h) }) : tr('reached_' + k));
    if (n && n.kind === 'table') item('nab', x.left);
    else if (x.left) {
        if (x.left.action != null) item('action', x.left.action);
        if (x.left.limit != null) item(n && n.action ? 'limit' : 'pdu', x.left.limit);
    }
    if (left.length) parts.push(tr('out_left', { list: left.join(', ') }));
    return parts.join(' · ');
}
function meter(res) {
    const n = res.norm;
    const host = $('#havMeter');
    if (!n || n.kind === 'table') { host.hidden = true; return; }
    host.hidden = false;
    const top = Math.max((n.limit || 5) * 1.5, res.a8 * 1.08, 1);
    const pct = v => Math.min(100, (v / top) * 100);
    const fill = $('#havMeterFill');
    fill.style.inlineSize = pct(res.a8) + '%';
    fill.className = 'hav-meter-fill ' + res.level;
    const ma = $('#havMarkAction'), ml = $('#havMarkLimit');
    if (n.action) { ma.hidden = false; ma.style.insetInlineStart = pct(n.action) + '%'; $('#havMarkActionL').textContent = ltr(numLim(n.action)); } else ma.hidden = true;
    if (n.limit) { ml.hidden = false; ml.style.insetInlineStart = pct(n.limit) + '%'; $('#havMarkLimitL').textContent = ltr(numLim(n.limit)); } else ml.hidden = true;
}
function verdict(res) {
    const n = res.norm, box = $('#havVerdict');
    let cls = 'good', head, body;
    const anyValid = res.rows.some(x => x.valid);
    if (!anyValid) {
        cls = ''; head = tr('v_empty'); body = '';
    } else if (n.kind === 'table') {
        const vals = { a: withU(num(res.aeq), 'u_ms2'), lim: withU(numLim(res.limit), 'u_ms2'), t: dur(res.hours) };
        if (res.level === 'over') { cls = 'bad'; head = tr('v_table_over', vals); body = tr('adv_over'); }
        else { head = tr('v_table_ok', vals); body = tr('adv_ok'); }
    } else if (!n.action) {
        const vals = { lim: withU(numLim(n.limit), 'u_ms2'), db: withU(num(H.levelDb(n.limit), 0), 'u_db') };
        if (res.level === 'over') { cls = 'bad'; head = tr('v_pdu_over', vals); body = tr('adv_over'); }
        else { head = tr('v_pdu_ok', vals); body = tr('adv_ok'); }
    } else {
        const vals = { act: withU(numLim(n.action), 'u_ms2'), lim: withU(numLim(n.limit), 'u_ms2') };
        if (res.level === 'over') { cls = 'bad'; head = tr('v_over', vals); body = tr('adv_over'); }
        else if (res.level === 'action') { cls = 'warn'; head = tr('v_action', vals); body = tr('adv_action'); }
        else { head = tr('v_ok', vals); body = tr('adv_ok'); }
    }
    box.className = 'tc-msg hav-verdict' + (cls ? ' ' + cls : '');
    box.innerHTML = `<b>${esc(head)}</b>${body ? `<span>${esc(body)}</span>` : ''}`;
    return { cls, head, body };
}
let last = null;
function update(userEdit) {
    const res = H.assess(rowsForCore(), S.norm, opts());
    last = res;
    const n = res.norm;
    const rowsEls = document.querySelectorAll('#havRows .hav-row');
    res.rows.forEach((x, i) => { const p = rowsEls[i] && rowsEls[i].querySelector('.hav-out'); if (p) { p.textContent = rowOut(i, x, n); p.classList.toggle('dim', !x.valid); } });

    const any = res.rows.some(x => x.valid);
    $('#havA8').innerHTML = any ? `${esc(ltr(num(res.a8)))}<span class="u"> ${esc(tr('u_ms2'))}</span>` : '—';
    const sub = [];
    if (any && n.points) sub.push(tr('sum_points', { p: num(res.points, 0) }));
    if (any && n.db) sub.push(tr('sum_db', { db: ltr(num(res.db, 0)) }));
    if (any && n.kind === 'table') sub.push(tr('sum_aeq', { a: withU(num(res.aeq), 'u_ms2') }));
    if (any) sub.push(tr('sum_time', { t: dur(res.hours) }));
    $('#havSub').textContent = sub.join(' · ');
    meter(res);
    verdict(res);
    $('#havTotal').textContent = any ? tr('total_line', { a: withU(num(res.a8), 'u_ms2'), v: $('#havVerdict b').textContent }) : '';
    if (userEdit && any) T.done(res);
    store.save('state', pack());
}

/* ── нормы ───────────────────────────────────────────────────────────────── */
function renderNorm() {
    const n = H.norm(S.norm);
    $('#havNorm').value = S.norm;
    const src = $('#havNormSrc');
    src.innerHTML = `${esc(tr('norm_src'))} <a href="${esc(n.url)}" target="_blank" rel="noopener nofollow">${esc(srcOf(n))}</a>`;
    $('#havNormNote').textContent = tr('norm_note_' + S.norm);
    // Единица «дБ» — там, где норма записана в дБ (Россия); у остальных поле в м/с².
    const dbBox = $('#havUnitBox');
    dbBox.hidden = !n.db;
    if (!n.db && S.unit === 'db') setUnit('ms2', true);
    document.querySelectorAll('#havUnit .tc-seg-b').forEach(b => { const on = b.dataset.unit === S.unit; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on); });
}
function setUnit(u, silent) {
    if (u === S.unit) return;
    // Набранные числа переводятся, а не теряют смысл.
    for (const r of S.rows) {
        if (r.alt != null) { const x = r.a; r.a = r.alt; r.alt = x; continue; }
        const v = parse(r.a);
        if (v > 0) { r.alt = r.a; r.a = u === 'db' ? dbStr(v) : numA(Math.round(H.fromDb(v) * 100) / 100); }
    }
    S.unit = u;
    if (!silent) { renderRows(); update(true); }
}

/* ── лист для журнала (PDF и печать) ─────────────────────────────────────── */
function el(tag, cls, text) { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; }
/* Латиница с цифрами и знаками («SanPiN 1.2.3685-21», формула) на арабском листе:
   html2canvas рисует слово за словом, а примитив PDF для rtl-листа рисует КАЖДОЕ
   слово справа налево — «5349-1» выходит «1-5349» и наползает на соседа (замер
   21.10.2026). Такой кусок рисуем сами на холсте слева направо — его html2canvas
   переносит картинкой. На ltr-листе это обычный текст. */
const FORMULA = 'ISO 5349-1: A_i(8) = a·√(T / 8 h),  A(8) = √ΣA_i(8)²;  HSE = 2·a²·T(h)';
function ltrInk(text, px = 12) {
    if (!RTL) return document.createTextNode(text);
    const c = document.createElement('canvas');
    const k = 2;
    const g = c.getContext('2d');
    const font = `${px}px Arial, Helvetica, sans-serif`;
    g.font = font;
    const w = Math.ceil(g.measureText(text).width) + 2, h = Math.ceil(px * 1.4);
    c.width = w * k; c.height = h * k;
    c.style.width = w + 'px'; c.style.height = h + 'px';
    c.className = 'ink';
    const d = c.getContext('2d');
    d.scale(k, k);
    d.font = font; d.fillStyle = '#000'; d.textBaseline = 'middle'; d.direction = 'ltr';
    d.fillText(text, 1, h / 2);
    return c;
}
function readRec() {
    for (const k of Object.keys(S.rec)) { const f = $('#havRec_' + k); if (f) S.rec[k] = f.value.trim(); }
}
function sheet() {
    readRec();
    const res = last || H.assess(rowsForCore(), S.norm, opts());
    const n = res.norm;
    const sh = el('div', 'hav-sheet');
    sh.setAttribute('dir', RTL ? 'rtl' : 'ltr');
    sh.append(el('h2', null, tr('sheet_title')));
    const hd = el('table', 'hd');
    const cells = [
        [tr('sheet_name'), S.rec.name], [tr('sheet_job'), S.rec.job], [tr('sheet_site'), S.rec.site],
        [tr('sheet_date'), S.rec.date], [tr('sheet_by'), S.rec.by], [tr('sheet_norm'), tr('norm_' + S.norm)],
    ];
    for (let i = 0; i < cells.length; i += 3) {
        const tr_ = el('tr');
        for (const [l, v] of cells.slice(i, i + 3)) { const td = el('td'); td.append(el('b', null, l), document.createTextNode(v || ' ')); tr_.append(td); }
        hd.append(tr_);
    }
    // Документ нормы — отдельной строкой ячейки: в rtl он рисуется холстом.
    const normTd = hd.rows[1].cells[2];
    normTd.append(el('br'), ltrInk(srcOf(n)));
    sh.append(hd);
    const tb = el('table', 'rows');
    const head = el('tr');
    const cols = [tr('col_tool'), tr('col_a'), tr('col_time')];
    if (n.points) cols.push(tr('col_points'));
    cols.push(tr('col_a8'));
    cols.forEach(c => head.append(el('th', null, c)));
    tb.append(head);
    S.rows.forEach((r, i) => {
        const x = res.rows[i];
        if (!x || !x.valid) return;
        const row = el('tr');
        const name = r.name || tr('ph_name');
        row.append(el('td', null, name + (r.declared ? ' ' + tr('sheet_declared') : '')));
        row.append(el('td', 'n', numA(x.a)));
        row.append(el('td', 'n', dur(x.hours)));
        if (n.points) row.append(el('td', 'n', num(x.points, 0)));
        row.append(el('td', 'n', num(x.a8)));
        tb.append(row);
    });
    const tot = el('tr', 'tot');
    tot.append(el('td', null, tr('sheet_total')), el('td'), el('td', 'n', dur(res.hours)));
    if (n.points) tot.append(el('td', 'n', num(res.points, 0)));
    tot.append(el('td', 'n', num(res.a8) + (n.db ? ' (' + num(res.db, 0) + ' ' + tr('u_db') + ')' : '')));
    tb.append(tot);
    sh.append(tb);
    const v = verdict(res);
    const vb = el('p', 'verdict ' + v.cls, v.head);
    sh.append(vb);
    if (v.body) sh.append(el('p', 'note', v.body));
    sh.append(el('p', 'note', tr('norm_note_' + S.norm)));
    if (S.rows.some(r => r.declared)) sh.append(el('p', 'note', tr('sheet_declared_note', { k: numA(S.factor), floor: S.floor ? tr('sheet_floor_on') : '' })));
    if (RTL) { const m = el('p', 'note', tr('sheet_method_label') + ' '); m.append(ltrInk(FORMULA)); sh.append(m); }
    else sh.append(el('p', 'note', tr('sheet_method')));
    sh.append(el('p', 'note small', tr('disclaimer')));
    const sign = el('div', 'sign');
    sign.append(el('div', null, tr('sheet_sign_worker')), el('div', null, tr('sheet_sign_by')), el('div', null, tr('sheet_date')));
    sh.append(sign);
    return sh;
}
async function makePdf() {
    T.touch('pdf');
    /* Лист — в документе (внутри скрытого #havPrint), а не отцепленным узлом: по
       вычисленному direction ПРИКРЕПЛЁННОГО узла примитив решает, рисовать ли
       строки справа налево; у отцепленного direction пуст, и арабский лист выходил
       с наползающими строками. html2pdf рисует копию листа, а не сам узел. */
    const host = $('#havPrint');
    host.textContent = '';
    const sh = sheet();
    host.append(sh);
    try {
        const res = await window.__generatePDF({
            element: sh, asIs: true, orientation: 'portrait',
            filename: `hand-arm-vibration-${S.rec.date || today()}.pdf`,
            strings: { genError: tr('pdf_error'), libError: tr('pdf_error') },
        });
        if (res && res.ok === false) window.__toast && window.__toast(tr('pdf_error'), 'error');
    } catch (err) {
        if (window.__handleError) window.__handleError(err, 'hav_pdf', { say: tr('pdf_error') });
    }
}
function doPrint() {
    T.touch('print');
    const host = $('#havPrint');
    host.textContent = '';
    host.append(sheet());
    window.print();
}
async function copyLink() {
    T.touch('link');
    const url = shareUrl();
    try {
        await navigator.clipboard.writeText(url);
        if (window.__toast) window.__toast(tr('link_copied'), 'success');
    } catch (e) {
        history.replaceState(null, '', url);
        if (window.__toast) window.__toast(tr('link_in_bar'), 'info');
    }
}

/* ── события ─────────────────────────────────────────────────────────────── */
function rowOf(node) {
    const box = node.closest('.hav-row');
    if (!box) return null;
    const k = Number(box.dataset.k);
    return S.rows.find(r => r.k === k) || null;
}
function addFromLib() {
    const id = $('#havLib').value;
    const t = TOOLS[id];
    const r = newRow(t ? { lib: id, name: tr('tl_' + id), a: S.unit === 'db' ? dbStr(t.a) : numA(t.a), h: '', m: '30' } : { m: '30' });
    S.rows.push(r);
    T.touch('add_' + (t ? 'lib' : 'own'));
    renderRows();
    update(true);
    const box = document.querySelector(`#havRows .hav-row[data-k="${r.k}"]`);
    const f = box && box.querySelector(t ? '[data-f="m"]' : '[data-f="name"]');
    if (f) { try { f.focus({ preventScroll: true }); } catch (e) { f.focus(); } box.scrollIntoView({ block: 'nearest' }); }
}
function act(a) {
    if (a === 'add') addFromLib();
    else if (a === 'pdf') makePdf();
    else if (a === 'print') doPrint();
    else if (a === 'link') copyLink();
    else if (a === 'clear') {
        S.rows = [];
        T.touch('clear');
        renderRows();
        update(true);
    }
}
function bind() {
    const rows = $('#havRows');
    rows.addEventListener('input', e => {
        const f = e.target.dataset.f;
        const r = rowOf(e.target);
        if (!r || !f) return;
        r[f] = f === 'declared' ? e.target.checked : e.target.value;
        if (f === 'a') r.alt = null;
        update(true);
    });
    rows.addEventListener('change', e => {
        if (e.target.dataset.f === 'declared') { const r = rowOf(e.target); if (r) { r.declared = e.target.checked; update(true); } }
    });
    rows.addEventListener('click', e => {
        const b = e.target.closest('button');
        if (!b) return;
        const r = rowOf(b);
        if (!r) return;
        if (b.dataset.act === 'del') {
            S.rows = S.rows.filter(x => x !== r);
            renderRows();
            update(true);
        } else if (b.dataset.mode) {
            if (r.mode === b.dataset.mode) return;
            // Переключение не выбрасывает набранное время: оно пересчитывается.
            const hrs = H.rowHours({ mode: r.mode, h: parse(r.h) || 0, m: parse(r.m) || 0, ops: parse(r.ops) || 0, sec: parse(r.sec) || 0 });
            r.mode = b.dataset.mode;
            if (r.mode === 'hm' && hrs > 0) { r.h = String(Math.floor(hrs)); r.m = String(Math.round((hrs - Math.floor(hrs)) * 60)); }
            T.touch('ops_mode');
            renderRows();
            update(true);
        }
    });
    $('#havNorm').addEventListener('change', e => {
        if (!H.norm(e.target.value)) return;
        S.norm = e.target.value;
        T.touch('norm_' + S.norm);
        renderNorm();
        update(true);
    });
    $('#havUnit').addEventListener('click', e => {
        const b = e.target.closest('[data-unit]');
        if (b) { setUnit(b.dataset.unit); renderNorm(); }
    });
    $('#havFactor').addEventListener('change', e => { S.factor = Number(e.target.value) || 1; update(true); });
    $('#havFloor').addEventListener('change', e => { S.floor = e.target.checked; update(true); });
    document.querySelectorAll('#hav [data-early-action]').forEach(b => b.addEventListener('click', () => act(b.dataset.earlyAction)));
    for (const k of Object.keys(S.rec)) {
        const f = $('#havRec_' + k);
        if (f) f.addEventListener('change', () => { readRec(); store.save('rec', S.rec); });
    }
}

/* ── то, что человек набрал до скрипта ───────────────────────────────────── */
function adoptDom() {
    const out = [];
    document.querySelectorAll('#havRows .hav-row').forEach(box => {
        const r = newRow({ lib: box.dataset.lib || '' });
        box.querySelectorAll('[data-f]').forEach(f => { r[f.dataset.f] = f.type === 'checkbox' ? f.checked : f.value; });
        out.push(r);
    });
    return out;
}

function boot() {
    try {
        const saved = store.load('state', null);
        const rec = store.load('rec', null);
        if (rec && typeof rec === 'object') for (const k of Object.keys(S.rec)) if (typeof rec[k] === 'string') S.rec[k] = rec[k].slice(0, 120);
        if (!fromHash() && !(saved && unpack(saved))) {
            S.norm = $('#havNorm').value || S.norm;
            S.rows = adoptDom();
        }
        for (const k of Object.keys(S.rec)) { const f = $('#havRec_' + k); if (f && S.rec[k]) f.value = S.rec[k]; }
        const dt = $('#havRec_date');
        if (dt && !dt.value) dt.value = today();
        $('#havFactor').value = String(S.factor);
        $('#havFloor').checked = S.floor;
        bind();
        renderNorm();
        renderRows();
        update(false);
        if (window.__earlyAction) window.__earlyAction.claim(SLUG, a => act(a));
        if (window.__stage) window.__stage('ready');
    } catch (err) {
        if (window.__earlyAction) window.__earlyAction.claim(SLUG);
        if (window.__handleError) window.__handleError(err, 'hav_boot', { say: tr('load_error') });
        const b = $('#havVerdict');
        if (b) b.textContent = tr('load_error');
    }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
